import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import GranjaMovimientosLinkButton from '../../components/granja/GranjaMovimientosLinkButton';
import GranjaRecetaCodigoField from '../../components/granja/GranjaRecetaCodigoField';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import granjaService, { GranjaConfig } from '../../services/granjaService';
import produccionService, {
  PreviewConfirmacionProduccionDto,
} from '../../services/produccionService';
import recetaService from '../../services/recetaService';
import { toLocalDateStringYMD } from '../../utils/formatters';

const GranjaAlimento: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();
  const [config, setConfig] = useState<GranjaConfig | null>(null);
  const [codigoReceta, setCodigoReceta] = useState('');
  const [kg, setKg] = useState('');
  const [preview, setPreview] = useState<PreviewConfirmacionProduccionDto | null>(null);
  const [codigoOp, setCodigoOp] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [elaborando, setElaborando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await granjaService.getConfig();
        setConfig(cfg);
        if (cfg.recetaMixerDefault) setCodigoReceta(cfg.recetaMixerDefault);
        if (cfg.batchKgDefault) setKg(String(cfg.batchKgDefault));
      } catch {
        enqueueSnackbar('Error al cargar configuración', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  const handlePreview = async () => {
    if (!config?.depositoGranja) {
      enqueueSnackbar('Configurá el depósito Granja', { variant: 'warning' });
      return;
    }
    const cantidad = Number(kg) || 0;
    if (!codigoReceta.trim() || cantidad <= 0) {
      enqueueSnackbar('Indicá receta y kilos', { variant: 'warning' });
      return;
    }
    try {
      setLoading(true);
      setPreview(null);
      setCodigoOp(null);
      const receta = await recetaService.getReceta(codigoReceta.trim());
      const codigoPt = receta.productoTerminado?.codigoArticulo;
      if (!codigoPt) {
        enqueueSnackbar('La receta no tiene producto terminado', { variant: 'error' });
        return;
      }
      const orden = await produccionService.create({
        codigoArticulo: codigoPt,
        cantidad,
        depositoOrigen: config.depositoGranja,
        depositoDestino: config.depositoGranja,
        observaciones: `Mixer granja — receta ${codigoReceta}`,
        fechaInicio: diaOperativo,
      });
      await produccionService.iniciar(orden.codigoProduccion);
      const prev = await produccionService.previewConfirmacion(orden.codigoProduccion, {
        cantidadProducida: cantidad,
        cantidadRechazada: 0,
      });
      setCodigoOp(orden.codigoProduccion);
      setPreview(prev);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al preparar preview';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleElaborar = async () => {
    if (!codigoOp || !preview) {
      enqueueSnackbar('Primero generá el preview', { variant: 'warning' });
      return;
    }
    const insuficientes = (preview.consumos ?? []).filter(
      (c) => c.controlaStock !== false && !c.suficiente,
    );
    if (insuficientes.length > 0) {
      enqueueSnackbar('Hay insumos con stock insuficiente', { variant: 'error' });
      return;
    }
    try {
      setElaborando(true);
      const cantidad = Number(kg) || 0;
      await produccionService.confirmar(codigoOp, {
        cantidadProducida: cantidad,
        cantidadRechazada: 0,
      });
      enqueueSnackbar(`Alimento elaborado: ${cantidad} kg`, { variant: 'success' });
      setPreview(null);
      setCodigoOp(null);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al confirmar producción';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setElaborando(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Alimento / Mixer
              </Typography>
              <GranjaMovimientosLinkButton />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Elabora alimento según la fórmula (receta) y descuenta materias primas.
            </Typography>

            <GranjaRecetaCodigoField
              label="Receta"
              value={codigoReceta}
              size="medium"
              dialogTitle="Receta de alimento"
              helperText="Buscá por código o descripción, o abrí el buscador"
              onChange={(codigo) => {
                setCodigoReceta(codigo);
                setPreview(null);
                setCodigoOp(null);
              }}
            />
            <TextField
              label="Kg a elaborar"
              type="number"
              value={kg}
              onChange={(e) => setKg(e.target.value)}
              fullWidth
            />

            <Button
              variant="outlined"
              size="large"
              disabled={loading}
              onClick={handlePreview}
              sx={{ minHeight: 48 }}
            >
              {loading ? 'Calculando…' : 'Ver consumos'}
            </Button>

            {preview ? (
              <>
                {(preview.consumos ?? []).some(
                  (c) => c.controlaStock !== false && !c.suficiente,
                ) ? (
                  <Alert severity="error">Hay insumos con stock insuficiente</Alert>
                ) : (
                  <Alert severity="success">Stock suficiente para elaborar</Alert>
                )}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Artículo</TableCell>
                      <TableCell align="right">Requerido</TableCell>
                      <TableCell align="right">Disponible</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(preview.consumos ?? []).map((c) => (
                      <TableRow key={c.codigoArticulo}>
                        <TableCell>
                          {c.codigoArticulo}
                          {c.descripcion ? ` — ${c.descripcion}` : ''}
                        </TableCell>
                        <TableCell align="right">{c.cantidadRequerida}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: c.suficiente ? undefined : 'error.main' }}
                        >
                          {c.stockDisponible}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={elaborando}
                  onClick={handleElaborar}
                  sx={{ minHeight: 56, fontWeight: 700 }}
                >
                  {elaborando ? 'Elaborando…' : 'Elaborar'}
                </Button>
              </>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GranjaAlimento;
